import React, { useState, useEffect } from "react";
import Modal from "../ui/Modal";
import { Check, X, Pen, Eraser, Users, Plus } from "lucide-react";
import { ApiService } from "../../services/api";
import WhiteboardShareModal, { WhiteboardShareFormData } from "./WhiteboardShareModal";

interface Participant {
  id: string;
  type: "USER" | "DEPARTMENT";
  user?: any;
  department?: any;
  canDraw: boolean;
  canErase?: boolean;
  hasJoined?: boolean;
  joinedAt?: string;
  users?: any[];
}

interface WhiteboardParticipantsModalProps {
  isOpen: boolean;
  onClose: () => void;
  whiteboardId: string;
  whiteboardName: string;
  onParticipantUpdate?: () => void;
  container?: HTMLElement | null;
}

export default function WhiteboardParticipantsModal({
  isOpen,
  onClose,
  whiteboardId,
  whiteboardName,
  onParticipantUpdate,
  container,
}: WhiteboardParticipantsModalProps) {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  const loadParticipants = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getWhiteboardParticipants(whiteboardId);
      setParticipants(response || []);
    } catch (error) {
      console.error("Katılımcılar yüklenemedi:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && whiteboardId) {
      loadParticipants();
    }
  }, [isOpen, whiteboardId]);

  const updateParticipantState = (
    participantId: string,
    updater: (participant: Participant) => Participant
  ) => {
    setParticipants((prev) =>
      prev.map((participant) =>
        participant.id === participantId ? updater(participant) : participant
      )
    );
  };

  const handlePermissionChange = async (participantId: string, value: boolean, participantType?: "USER" | "DEPARTMENT") => {
    setUpdating(participantId);
    try {
      await ApiService.updateWhiteboardParticipant(whiteboardId, participantId, {
        canDraw: value,
      });

      if (participantType === "DEPARTMENT") {
        updateParticipantState(participantId, (participant) => {
          const previousCanDraw = participant.canDraw;

          return {
            ...participant,
            canDraw: value,
            users: participant.users?.map((user) => {
              const inheritsDepartmentCanDraw = user.canDraw === previousCanDraw;
              return inheritsDepartmentCanDraw ? { ...user, canDraw: value } : user;
            }),
          };
        });
      } else {
        updateParticipantState(participantId, (participant) => ({
          ...participant,
          canDraw: value,
        }));
      }
    } catch (error) {
      console.error("Yetki güncellenemedi:", error);
    } finally {
      setUpdating(null);
    }
  };

  const handleErasePermissionChange = async (participantId: string, value: boolean, participantType?: "USER" | "DEPARTMENT") => {
    setUpdating(participantId);
    try {
      await ApiService.updateWhiteboardParticipant(whiteboardId, participantId, {
        canErase: value,
      });

      if (participantType === "DEPARTMENT") {
        updateParticipantState(participantId, (participant) => {
          const previousCanErase = participant.canErase || false;

          return {
            ...participant,
            canErase: value,
            users: participant.users?.map((user) => {
              const inheritsDepartmentCanErase = (user.canErase || false) === previousCanErase;
              return inheritsDepartmentCanErase ? { ...user, canErase: value } : user;
            }),
          };
        });
      } else {
        updateParticipantState(participantId, (participant) => ({
          ...participant,
          canErase: value,
        }));
      }
    } catch (error) {
      console.error("Yetki güncellenemedi:", error);
    } finally {
      setUpdating(null);
    }
  };

  const handleRemoveParticipant = async (participantId: string) => {
    setUpdating(participantId);
    try {
      await ApiService.removeWhiteboardParticipant(whiteboardId, participantId);
      setParticipants((prev) => prev.filter((p) => p.id !== participantId));
      onParticipantUpdate?.();
    } catch (error) {
      console.error("Katılımcı silinemedi:", error);
    } finally {
      setUpdating(null);
    }
  };

  const handleUpdateDepartmentUserPermission = async (
    participantId: string,
    userId: string,
    value: boolean
  ) => {
    setUpdating(userId);
    try {
      const result = await ApiService.updateDepartmentUserPermission(
        whiteboardId,
        participantId,
        userId,
        { canDraw: value }
      );

      updateParticipantState(participantId, (participant) => ({
        ...participant,
        users: participant.users?.map((user) =>
          user.id === userId
            ? {
                ...user,
                canDraw: result?.canDraw ?? value,
                canErase: result?.canErase ?? user.canErase,
                hasCustomPermission: true,
              }
            : user
        ),
      }));
    } catch (error) {
      console.error("Kullanıcı yetkisi güncellenemedi:", error);
    } finally {
      setUpdating(null);
    }
  };

  const handleUpdateDepartmentUserErasePermission = async (
    participantId: string,
    userId: string,
    value: boolean
  ) => {
    setUpdating(userId);
    try {
      const result = await ApiService.updateDepartmentUserPermission(
        whiteboardId,
        participantId,
        userId,
        { canErase: value }
      );

      updateParticipantState(participantId, (participant) => ({
        ...participant,
        users: participant.users?.map((user) =>
          user.id === userId
            ? {
                ...user,
                canDraw: result?.canDraw ?? user.canDraw,
                canErase: result?.canErase ?? value,
                hasCustomPermission: true,
              }
            : user
        ),
      }));
    } catch (error) {
      console.error("Kullanıcı yetkisi güncellenemedi:", error);
    } finally {
      setUpdating(null);
    }
  };

  const handleExcludeDepartmentUser = async (participantId: string, userId: string) => {
    setUpdating(userId);
    try {
      await ApiService.excludeDepartmentUser(whiteboardId, participantId, userId);
      updateParticipantState(participantId, (participant) => ({
        ...participant,
        users: participant.users?.filter((user) => user.id !== userId),
      }));
    } catch (error) {
      console.error("Kullanıcı çıkarılamadı:", error);
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("tr-TR", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleAddParticipants = async (formData: WhiteboardShareFormData) => {
    setAddLoading(true);
    try {
      const newParticipants: any[] = [];

      if (formData.type === "USER") {
        formData.selectedUsers.forEach((item) => {
          newParticipants.push({
            type: "USER",
            userId: item.id,
            canDraw: item.canDraw,
            canErase: item.canErase,
          });
        });
      } else {
        formData.selectedDepartments.forEach((item) => {
          newParticipants.push({
            type: "DEPARTMENT",
            departmentId: item.id,
            canDraw: item.canDraw,
            canErase: item.canErase,
          });
        });
      }

      await ApiService.shareWhiteboard(whiteboardId, newParticipants);
      await loadParticipants();
      onParticipantUpdate?.();
      setShowAddModal(false);
    } catch (error) {
      console.error("Katılımcı eklenemedi:", error);
    } finally {
      setAddLoading(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={`Katılımcılar - ${whiteboardName}`} size="lg" container={container}>
        <div className="space-y-4">

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium text-sm"
            >
              <Plus size={16} />
              Katılımcı Ekle
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          ) : participants.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="mx-auto h-12 w-12 text-gray-300 mb-3" />
              <p>Henüz katılımcı eklenmemiş</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {participants.map((participant) => (
                <div
                  key={participant.id}
                  className={`p-4 rounded-lg border ${participant.type === "USER" ? "bg-blue-50 border-blue-200" : "bg-green-50 border-green-200"
                    }`}
                >
                  {participant.type === "USER" ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {participant.user?.media?.url ? (
                          <img
                            src={participant.user.media.url}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-medium">
                            {participant.user?.firstName?.[0] || "?"}
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-gray-900">
                            {`${participant.user?.firstName || ""} ${participant.user?.lastName || ""}`.trim() || "İsimsiz"}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-2">
                            {participant.hasJoined ? (
                              <span className="inline-flex items-center gap-1 text-green-600">
                                <Check size={12} />
                                Katıldı
                                {participant.joinedAt && (
                                  <span className="text-gray-400 ml-1">({formatDate(participant.joinedAt)})</span>
                                )}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-amber-600">
                                <X size={12} />
                                Henüz katılmadı
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1.5 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={participant.canDraw}
                            onChange={(e) => handlePermissionChange(participant.id, e.target.checked, participant.type)}
                            disabled={updating === participant.id}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <Pen size={16} className="text-gray-500 group-hover:text-blue-600 transition-colors" />
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer group ml-2">
                          <input
                            type="checkbox"
                            checked={participant.canErase || false}
                            onChange={(e) => handleErasePermissionChange(participant.id, e.target.checked, participant.type)}
                            disabled={updating === participant.id}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <Eraser size={16} className="text-gray-500 group-hover:text-blue-600 transition-colors" />
                        </label>
                        <button
                          type="button"
                          onClick={() => handleRemoveParticipant(participant.id)}
                          disabled={updating === participant.id}
                          className="text-gray-400 hover:text-red-600 p-1 disabled:opacity-50 transition-colors"
                          title="Katılımcıyı Kaldır"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {participant.department?.color && (
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: participant.department.color }}
                            />
                          )}
                          <span className="font-medium text-gray-900">{participant.department?.name || "Departman"}</span>
                          <span className="text-sm text-gray-500">({participant.users?.length || 0} kişi)</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-1.5 cursor-pointer group">
                            <input
                              type="checkbox"
                              checked={participant.canDraw}
                              onChange={(e) => handlePermissionChange(participant.id, e.target.checked, participant.type)}
                              disabled={updating === participant.id}
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <Pen size={16} className="text-gray-500 group-hover:text-green-600 transition-colors" />
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer group ml-2">
                            <input
                              type="checkbox"
                              checked={participant.canErase || false}
                              onChange={(e) => handleErasePermissionChange(participant.id, e.target.checked, participant.type)}
                              disabled={updating === participant.id}
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <Eraser size={16} className="text-gray-500 group-hover:text-green-600 transition-colors" />
                          </label>
                          <button
                            type="button"
                            onClick={() => handleRemoveParticipant(participant.id)}
                            disabled={updating === participant.id}
                            className="text-gray-400 hover:text-red-600 p-1 disabled:opacity-50 transition-colors"
                            title="Departmanı Kaldır"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                      {participant.users && participant.users.length > 0 && (
                        <div className="pl-6 border-l-2 border-green-200 space-y-2 mt-2">
                          <p className="text-xs text-gray-500 mb-2">
                            Kullanıcılara özel yetki vermek için checkbox'ları kullanın:
                          </p>
                          {participant.users.map((user: any) => {
                            return (
                              <div key={user.id} className="flex items-center justify-between py-1">
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  {user.media?.url ? (
                                    <img src={user.media.url} alt="" className="w-6 h-6 rounded-full object-cover" />
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-green-200 flex items-center justify-center text-green-700 text-xs">
                                      {user.firstName?.[0] || "?"}
                                    </div>
                                  )}
                                  <div className="flex flex-col">
                                    <div className="flex items-center gap-2">
                                      <span>{`${user.firstName || ""} ${user.lastName || ""}`.trim() || "İsimsiz"}</span>
                                      {user.hasJoined ? (
                                        <span className="inline-flex items-center gap-0.5 text-xs text-green-600" title={user.joinedAt ? formatDate(user.joinedAt) : undefined}>
                                          <Check size={10} />
                                          Katıldı
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-0.5 text-xs text-amber-600">
                                          <X size={10} />
                                          Katılmadı
                                        </span>
                                      )}
                                    </div>
                                    {user.department && user.department.id !== participant.department?.id && (
                                      <span className="text-xs text-gray-400">{user.department.name}</span>
                                    )}
                                  </div>
                                  {user.hasCustomPermission && (
                                    <span className="text-xs text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">Özel yetki</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <label className="flex items-center gap-1 cursor-pointer group">
                                    <input
                                      type="checkbox"
                                      checked={user.canDraw || false}
                                      onChange={(e) =>
                                        handleUpdateDepartmentUserPermission(
                                          participant.id,
                                          user.id,
                                          e.target.checked
                                        )
                                      }
                                      disabled={updating === user.id}
                                      className="h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <Pen size={14} className="text-gray-400 group-hover:text-blue-600 transition-colors" />
                                  </label>
                                  <label className="flex items-center gap-1 cursor-pointer group ml-2">
                                    <input
                                      type="checkbox"
                                      checked={user.canErase || false}
                                      onChange={(e) =>
                                        handleUpdateDepartmentUserErasePermission(
                                          participant.id,
                                          user.id,
                                          e.target.checked
                                        )
                                      }
                                      disabled={updating === user.id}
                                      className="h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <Eraser size={14} className="text-gray-400 group-hover:text-blue-600 transition-colors" />
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => handleExcludeDepartmentUser(participant.id, user.id)}
                                    disabled={updating === user.id}
                                    className="text-gray-400 hover:text-red-600 p-0.5 disabled:opacity-50 transition-colors"
                                    title="Kullanıcıyı çıkar"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 font-medium"
            >
              Kapat
            </button>
          </div>
        </div>
      </Modal>

      <WhiteboardShareModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddParticipants}
        isLoading={addLoading}
        title="Katılımcı Ekle"
        container={container}
      />
    </>
  );
}
